import React, { useState } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import StatsSectionHeader from './StatsSectionHeader';
import StatsDrillDialog from './StatsDrillDialog';
import { belgianDates } from './dateFormat';

const ROW_HEIGHT = 34;
const HEADER_HEIGHT = 40;
const TOOLBAR_HEIGHT = 46;
const MAX_HEIGHT = 520;

/** Colour class for a signed number: red when it moves the wrong way, green when it moves the right way. */
function signClass(sign, value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v === 0) return '';
  const up = v > 0;
  const bad = sign === 'bad_up' ? up : !up;
  return bad ? 'stats__bad' : 'stats__good';
}

/** Builds the drill parameters for a clicked row from the table's drill spec. */
function drillParams(drill, row) {
  const params = {};
  const fields = drill.fields || [];
  if (Array.isArray(fields)) {
    fields.forEach((f) => { params[f] = row[f]; });
  } else {
    Object.keys(fields).forEach((param) => { params[param] = row[fields[param]]; });
  }
  Object.assign(params, drill.fixed || {});
  return params;
}

/**
 * Renders one table spec from the backend:
 *   {key, label, description?, columns: [{field, label, type?}], rows: [{id, ...}], sql?, drill?}
 * The grid grows with its rows (no inner scrollbar for short tables) and caps at maxHeight.
 * The grid toolbar gives filtering and CSV export for free. When the spec carries a drill,
 * clicking a row opens the detail behind it (StatsDrillDialog).
 */
export default function StatsTable({ table, theme, loading = false, showSql = false, t, drillContext = null, maxHeight = MAX_HEIGHT }) {
  const [drillRow, setDrillRow] = useState(null);
  if (!table) return null;
  const drill = table.drill && drillContext ? table.drill : null;
  const columns = (table.columns || []).map((c, i) => ({
    field: c.field,
    headerName: c.label,
    type: c.type === 'number' ? 'number' : 'string',
    minWidth: c.type === 'number' ? 110 : 160,
    flex: i === 0 || c.type !== 'number' ? 1 : 0,
    headerAlign: c.type === 'number' ? 'right' : 'left',
    align: c.type === 'number' ? 'right' : 'left',
    valueFormatter: c.type === 'number' ? undefined : (value) => belgianDates(value),
    // sign: 'bad_up' (more is worse: red above 0, green below) or 'good_up' (the opposite).
    cellClassName: c.sign ? (params) => signClass(c.sign, params.value) : undefined,
  }));
  const rows = (table.rows || []).map((r, i) => ({ id: typeof r.id !== 'undefined' ? r.id : i, ...r }));
  const height = Math.min(maxHeight, HEADER_HEIGHT + TOOLBAR_HEIGHT + ROW_HEIGHT * Math.max(1, rows.length) + 4);
  const tr = (k, fallback) => (t ? t(k) : null) || fallback;
  const description = drill
    ? `${table.description || ''} ${tr('stats_click_row', 'Klik op een rij voor het detail.')}`.trim()
    : table.description;

  return (
    <div style={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
      <StatsSectionHeader label={table.label} count={rows.length} description={description} sql={table.sql} showSql={showSql} theme={theme} t={t} />
      {/* minHeight fits the rows; flex lets the grid fill the panel when a neighbour on the same row is taller */}
      <div style={{ minHeight: height, flex: '1 1 auto', width: '100%' }}>
        <DataGridPro
          className={theme}
          columns={columns}
          rows={rows}
          rowHeight={ROW_HEIGHT}
          columnHeaderHeight={HEADER_HEIGHT}
          density="compact"
          loading={loading}
          pagination={false}
          showToolbar
          disableRowSelectionOnClick
          hideFooter
          onRowClick={drill ? (params) => setDrillRow(params.row) : undefined}
          sx={drill ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : undefined}
        />
      </div>
      {drill && (
        <StatsDrillDialog
          open={drillRow !== null}
          onClose={() => setDrillRow(null)}
          drill={drill.name}
          params={drillRow ? drillParams(drill, drillRow) : {}}
          context={drillContext}
          theme={theme}
          t={t}
          showSql={showSql}
        />
      )}
    </div>
  );
}
